/**
 * Project Permissions Component - Manage user permissions for projects
 */
import React, { useState, useEffect } from 'react';
import { useAuthStore, User } from '../../stores/authStore';
import { useProjectStore } from '../../stores';
import { getErrorMessage } from '../../utils/errorMessages';
import './AdminPanel.css';

const API_URL = process.env.REACT_APP_API_URL || '';

type PermissionLevel = 'owner' | 'editor' | 'viewer' | 'no_access';

interface ProjectPermission {
  id: number;
  user_id: number;
  project_id: number;
  permission_level: PermissionLevel;
  user_email?: string;
  user_username?: string;
  user_full_name?: string;
  user_role?: string;
  project_name?: string;
}

interface ProjectWithPermissions {
  id: number;
  name: string;
  description?: string;
  permissions: ProjectPermission[];
}

export const ProjectPermissions: React.FC = () => {
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [projectPermissions, setProjectPermissions] = useState<ProjectWithPermissions | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddPermission, setShowAddPermission] = useState(false);

  const { token } = useAuthStore();
  const { projects } = useProjectStore();

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedProjectId) {
      fetchProjectPermissions(selectedProjectId);
    }
  }, [selectedProjectId]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Nepodarilo sa načítať používateľov');
      }

      const data = await response.json();
      setUsers(data);
    } catch (err: any) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchProjectPermissions = async (projectId: number) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/permissions/projects/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Nepodarilo sa načítať oprávnenia');
      }

      const data = await response.json();
      setProjectPermissions(data);
      setError(null);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Nepodarilo sa načítať oprávnenia.'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePermission = async (
    projectId: number,
    userId: number,
    newLevel: PermissionLevel
  ) => {
    try {
      const response = await fetch(
        `${API_URL}/api/permissions/projects/${projectId}/users/${userId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ permission_level: newLevel }),
        }
      );

      if (!response.ok) {
        throw new Error('Nepodarilo sa aktualizovať oprávnenie');
      }

      // Refresh permissions
      if (selectedProjectId) {
        await fetchProjectPermissions(selectedProjectId);
      }
    } catch (err: any) {
      alert(getErrorMessage(err, 'Nepodarilo sa aktualizovať oprávnenie.'));
    }
  };

  const handleDeletePermission = async (projectId: number, userId: number) => {
    if (!window.confirm('Naozaj chcete odobrať prístup tomuto používateľovi?')) {
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/permissions/projects/${projectId}/users/${userId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Nepodarilo sa odobrať oprávnenie');
      }

      // Refresh permissions
      if (selectedProjectId) {
        await fetchProjectPermissions(selectedProjectId);
      }
    } catch (err: any) {
      alert(getErrorMessage(err, 'Nepodarilo sa odobrať oprávnenie.'));
    }
  };

  const getPermissionLabel = (level: PermissionLevel): string => {
    const labels: Record<PermissionLevel, string> = {
      owner: 'Vlastník',
      editor: 'Editor',
      viewer: 'Pozorovateľ',
      no_access: 'Bez prístupu',
    };
    return labels[level];
  };

  const getPermissionBadgeClass = (level: PermissionLevel): string => {
    const classes: Record<PermissionLevel, string> = {
      owner: 'perm-badge-owner',
      editor: 'perm-badge-editor',
      viewer: 'perm-badge-viewer',
      no_access: 'perm-badge-none',
    };
    return classes[level];
  };

  const getRoleLabel = (role?: string): string => {
    switch (role) {
      case 'admin':
        return '👑 Admin';
      case 'engineer':
        return '🔧 Inžinier';
      case 'service_tech':
        return '🛠️ Servisný technik';
      case 'install_tech':
        return '📦 Montážny technik';
      case 'viewer':
        return '👁️ Pozorovateľ';
      case 'guest':
        return '👤 Hosť';
      default:
        return role || 'N/A';
    }
  };

  const getRoleBadgeClass = (role?: string): string => {
    switch (role) {
      case 'admin':
        return 'perm-badge-owner';
      case 'engineer':
        return 'perm-badge-editor';
      case 'service_tech':
        return 'perm-badge-editor';
      case 'install_tech':
        return 'perm-badge-viewer';
      case 'viewer':
        return 'perm-badge-viewer';
      case 'guest':
        return 'perm-badge-none';
      default:
        return '';
    }
  };

  return (
    <div className="project-permissions">
      <div className="management-header">
        <h3>🔐 Oprávnenia projektov</h3>
      </div>

      <div className="project-selector">
        <label>Vyberte projekt:</label>
        <select
          value={selectedProjectId || ''}
          onChange={(e) => setSelectedProjectId(Number(e.target.value))}
        >
          <option value="">-- Vyberte projekt --</option>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
      </div>

      {selectedProjectId && (
        <>
          {loading && <div className="loading">Načítavam oprávnenia...</div>}
          
          {error && <div className="error-message">⚠️ {error}</div>}

          {projectPermissions && !loading && (
            <>
              <div className="permissions-header">
                <h4>Používatelia s prístupom k projektu: {projectPermissions.name}</h4>
                <button
                  className="btn-primary"
                  onClick={() => setShowAddPermission(true)}
                >
                  + Pridať používateľa
                </button>
              </div>

              {projectPermissions.permissions.length === 0 ? (
                <div className="empty-state">
                  <p>Žiadni používatelia nemajú prístup k tomuto projektu.</p>
                  <p>Kliknite na "Pridať používateľa" pre pridanie prístupu.</p>
                </div>
              ) : (
                <div className="permissions-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Používateľ</th>
                        <th>Email</th>
                        <th>Globálna rola</th>
                        <th>Oprávnenie k projektu</th>
                        <th>Akcie</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projectPermissions.permissions.map((perm) => (
                        <tr key={perm.id}>
                          <td>{perm.user_full_name || perm.user_username}</td>
                          <td>{perm.user_email}</td>
                          <td>
                            <span className={`role-badge ${getRoleBadgeClass(perm.user_role)}`}>
                              {getRoleLabel(perm.user_role)}
                            </span>
                          </td>
                          <td>
                            <select
                              value={perm.permission_level}
                              onChange={(e) =>
                                handleUpdatePermission(
                                  perm.project_id,
                                  perm.user_id,
                                  e.target.value as PermissionLevel
                                )
                              }
                              className={`permission-select ${getPermissionBadgeClass(perm.permission_level)}`}
                            >
                              <option value="owner">Vlastník</option>
                              <option value="editor">Editor</option>
                              <option value="viewer">Pozorovateľ</option>
                              <option value="no_access">Bez prístupu</option>
                            </select>
                          </td>
                          <td>
                            <button
                              className="btn-icon btn-danger"
                              onClick={() =>
                                handleDeletePermission(perm.project_id, perm.user_id)
                              }
                              title="Odobrať prístup"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="permissions-legend">
                <h5>Vysvetlenie oprávnení:</h5>
                <ul>
                  <li><strong>Vlastník:</strong> Plná kontrola - môže mazať projekt a spravovať oprávnenia</li>
                  <li><strong>Editor:</strong> Môže upravovať všetko okrem oprávnení</li>
                  <li><strong>Pozorovateľ:</strong> Len čítanie - nemôže upravovať</li>
                  <li><strong>Bez prístupu:</strong> Žiadny prístup k projektu</li>
                </ul>
              </div>
            </>
          )}

          {showAddPermission && selectedProjectId && (
            <AddPermissionDialog
              projectId={selectedProjectId}
              projectName={projectPermissions?.name || ''}
              users={users}
              existingPermissions={projectPermissions?.permissions || []}
              onClose={() => setShowAddPermission(false)}
              onSuccess={() => {
                setShowAddPermission(false);
                if (selectedProjectId) {
                  fetchProjectPermissions(selectedProjectId);
                }
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

// Add Permission Dialog Component
interface AddPermissionDialogProps {
  projectId: number;
  projectName: string;
  users: User[];
  existingPermissions: ProjectPermission[];
  onClose: () => void;
  onSuccess: () => void;
}

const AddPermissionDialog: React.FC<AddPermissionDialogProps> = ({
  projectId,
  projectName,
  users,
  existingPermissions,
  onClose,
  onSuccess,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [permissionLevel, setPermissionLevel] = useState<PermissionLevel>('viewer');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { token } = useAuthStore();

  // Filter out users who already have permissions
  const existingUserIds = existingPermissions.map((p) => p.user_id);
  const availableUsers = users.filter((u) => !existingUserIds.includes(u.id));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedUserId) {
      setError('Vyberte používateľa');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/permissions/projects/${projectId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: selectedUserId,
          permission_level: permissionLevel,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Nepodarilo sa pridať oprávnenie');
      }

      onSuccess();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Nepodarilo sa pridať oprávnenie.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog small-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3>➕ Pridať prístup k projektu</h3>
          <button className="dialog-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="permission-form">
          {error && <div className="error-message">⚠️ {error}</div>}

          <div className="form-group">
            <label>Projekt</label>
            <input type="text" value={projectName} disabled />
          </div>

          <div className="form-group">
            <label>Používateľ *</label>
            {availableUsers.length === 0 ? (
              <p className="info-message">Všetci používatelia už majú prístup k tomuto projektu.</p>
            ) : (
              <select
                value={selectedUserId || ''}
                onChange={(e) => setSelectedUserId(Number(e.target.value))}
                required
              >
                <option value="">-- Vyberte používateľa --</option>
                {availableUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name || user.username} ({user.email})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group">
            <label>Oprávnenie k projektu *</label>
            <select
              value={permissionLevel}
              onChange={(e) => setPermissionLevel(e.target.value as PermissionLevel)}
            >
              <option value="viewer">Pozorovateľ (len čítanie)</option>
              <option value="editor">Editor (môže upravovať)</option>
              <option value="owner">Vlastník (plná kontrola)</option>
            </select>
            <div className="info-box" style={{
              marginTop: '10px',
              padding: '10px',
              backgroundColor: '#e3f2fd',
              borderLeft: '4px solid #2196f3',
              fontSize: '13px',
              lineHeight: '1.5'
            }}>
              <strong>ℹ️ Dôležité:</strong>
              <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
                <li><strong>Oprávnenie k projektu</strong> určuje čo môže používateľ robiť v tomto projekte</li>
                <li><strong>Globálna rola používateľa</strong> (nastavuje sa v Správe používateľov) určuje aký pohľad má:
                  <ul style={{ marginTop: '5px' }}>
                    <li><strong>Montážny technik (install_tech)</strong> - vidí zjednodušený checklist aj s Editor oprávnením</li>
                    <li><strong>Servisný technik (service_tech)</strong> - vidí plný technický detail</li>
                    <li><strong>Inžinier (engineer)</strong> - môže vytvárať projekty</li>
                  </ul>
                </li>
                <li>Pre montážnych technikov odporúčame <strong>Editor</strong> oprávnenie</li>
              </ul>
            </div>
          </div>

          <div className="dialog-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Zrušiť
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || availableUsers.length === 0}
            >
              {loading ? 'Pridávam...' : 'Pridať'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

